/**
 * Nombres de servicio, del lado de la pantalla.
 *
 * Es un espejo de backend/src/servicios.js, que es el que manda: el que rechaza
 * de verdad es el servidor, con un 409, porque el navegador se puede saltear.
 * Esto está acá para que el aviso llegue ANTES de mandar el formulario y quede
 * pegado al campo del nombre, en vez de completar veinte campos y recibir un
 * error general al apretar guardar.
 *
 * Las dos copias tienen que decir lo mismo. Si cambia la forma de comparar,
 * cambian las dos: si el navegador fuera más permisivo, el alta se rechazaría
 * recién en el servidor —que es correcto, pero se siente como una falla—, y si
 * fuera más estricto, rechazaría nombres que el servidor acepta, que es peor
 * porque no hay forma de guardarlos.
 */

/**
 * El nombre reducido a lo que lo hace distinto de otro: sin espacios de sobra,
 * sin mayúsculas y sin tildes. La puntuación y las palabras sí cuentan, así que
 * "Corte" y "Corte + Barba" siguen siendo dos servicios.
 */
export const normalizarNombre = (nombre) =>
  String(nombre ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();

/**
 * Los servicios que coinciden con lo que la persona escribió en el buscador.
 *
 * Esta función NO tiene espejo en el backend, y no lo necesita: buscar es algo
 * que pasa entero en la pantalla, sin nada que autorizar del otro lado. Lo que
 * sí comparte con el resto del archivo es `normalizarNombre`, y por eso vive
 * acá: escribir una segunda vez el "sin tildes, sin mayúsculas, sin espacios de
 * más" en otro lado es cómo se empiezan a comportar distinto dos búsquedas
 * sobre los mismos datos. (La regla de "las dos copias tienen que decir lo
 * mismo" es sobre `buscarNombreRepetido`, que sí es un espejo.)
 *
 * Busca por nombre Y por descripción: quien no se acuerda de cómo se llama el
 * servicio suele acordarse de qué incluye, y en un negocio con treinta
 * servicios ese es justamente el que no encuentra scrolleando.
 *
 * Cada palabra por separado, y todas tienen que aparecer: "corte barba"
 * encuentra "Corte + Barba", que con una sola comparación contra el texto
 * completo no aparecería (sobra el "+" en el medio). Así el orden en que se
 * escriben las palabras deja de importar.
 *
 * Sin texto devuelve la lista entera, no una vacía: el buscador arranca vacío y
 * eso no puede querer decir "ningún servicio".
 */
export const buscarServicios = (servicios, texto) => {
  const lista = Array.isArray(servicios) ? servicios : [];
  const palabras = normalizarNombre(texto).split(' ').filter(Boolean);
  if (palabras.length === 0) return lista;

  return lista.filter((servicio) => {
    const dondeBuscar = normalizarNombre(
      `${servicio?.name ?? ''} ${servicio?.description ?? ''}`
    );
    return palabras.every((palabra) => dondeBuscar.includes(palabra));
  });
};

/**
 * El servicio que ya se llama así, o `null` si el nombre está libre.
 *
 * `idQueSeEdita` deja fuera de la comparación al que se está editando, para que
 * guardarlo sin cambiarle el nombre no choque contra sí mismo.
 */
export const buscarNombreRepetido = (servicios, nombre, idQueSeEdita = null) => {
  const buscado = normalizarNombre(nombre);
  // Un nombre vacío no es "repetido": de eso se queja la validación de campos
  // obligatorios, y contestar acá daría el mensaje equivocado.
  if (!buscado) return null;

  const lista = Array.isArray(servicios) ? servicios : [];
  return (
    lista.find(
      (servicio) =>
        servicio &&
        (idQueSeEdita === null || String(servicio.id) !== String(idQueSeEdita)) &&
        normalizarNombre(servicio.name) === buscado
    ) || null
  );
};
